import { useCallback, useEffect, useState } from 'react';
import { appIconSchema, DEFAULT_APP_ICON, type AppIcon } from '@avslack/shared';
import { setDesktopAppIcon } from './desktop';

/**
 * A választott alkalmazás-ikon érvényesítése — minden felületen.
 *
 * A beállítás a FELHASZNÁLÓHOZ tartozik (szerver oldalon tárolva), hogy kövesse
 * a gépek között — pontosan úgy, mint a téma. A `localStorage` itt is
 * gyorsítótár: ebből állítjuk be a favicont még az első festés előtt, különben
 * minden betöltéskor felvillanna a petrol ikon a választott helyett.
 *
 * ─── Négy különböző felület, négy különböző mechanizmus ─────────────────────
 *
 *  1. **Böngészőfül**: a `<link rel="icon">` címe. Azonnal vált.
 *  2. **iOS kezdőképernyő**: az `apple-touch-icon`. A Safari a HOZZÁADÁS
 *     pillanatában lefagyasztja — a már kint lévő ikon soha nem frissül.
 *  3. **Telepített PWA**: a manifestből jön, amit a szerver állít össze. Ezért
 *     megy bele a `?ikon=` a manifest címébe ÉS a süti is: előbbi a most futó
 *     lapnak szól, utóbbi akkor dolgozik, amikor a böngésző magától
 *     ellenőrzi a manifestet, és a lapunk nem fut.
 *  4. **Asztali app**: a Tauri ablak- és tálcaikonja, futásidőben cserélve.
 *
 * A telepítő beégetett `.exe`-ikonja NEM változik — az fordítási idejű
 * erőforrás. A Start menü és a kitűzött parancsikon ezért marad az eredeti.
 */

const KEY = 'avslack.appIcon';

/**
 * A SÜTI, amiből a szerver dolgozik.
 *
 * Nem httpOnly, és ez szándékos: ezt a felület írja. A session sütijére nem
 * támaszkodhatunk, mert a manifestet a böngésző bejelentkezés nélkül,
 * magától is lekéri.
 */
const COOKIE = 'avslack.icon';

/**
 * A gyorsítótárazott választás — mellékhatás nélkül.
 *
 * A bejelentkező képernyőnek kell, ahol még nincs felhasználó: a `useAppIcon`
 * ott túlzás lenne, mert az újra érvényesítené az ikont mindenhol.
 */
export function readAppIcon(): AppIcon {
  try {
    const stored = localStorage.getItem(KEY);
    return appIconSchema.catch(DEFAULT_APP_ICON).parse(stored);
  } catch {
    // Privát ablak vagy letiltott tárolás — az alapértelmezés a tartalék.
    return DEFAULT_APP_ICON;
  }
}

function writeCookie(icon: AppIcon): void {
  try {
    const secure = window.location.protocol === 'https:' ? '; secure' : '';
    // Egy év: a választás ritkán változik, és a lejárat utáni visszaesés az
    // alapértelmezésre kellemetlen meglepetés lenne.
    document.cookie = `${COOKIE}=${icon}; path=/; max-age=31536000; samesite=lax${secure}`;
  } catch {
    // A süti hiánya nem törheti el a váltást: a fülön lévő ikon így is vált,
    // csak a telepített app marad a régin.
  }
}

function setLink(selector: string, href: string): void {
  const link = document.head.querySelector<HTMLLinkElement>(selector);
  if (link) link.href = href;
}

/** Az ikon érvényesítése mindenhol, ahol a böngésző engedi. */
function apply(icon: AppIcon): void {
  setLink('link[rel="icon"]', `/icons/${icon}/icon-32.png`);
  setLink('link[rel="apple-touch-icon"]', `/icons/${icon}/icon-180.png`);

  /*
    A MANIFEST CÍME IS VÁLTOZIK.

    A szerver a sütiből is meg tudná állapítani, de a lekérdezési paraméter
    egyértelműbb jelzés a böngésző felé: a manifest tartalma más lett, mint
    amit legutóbb látott. Az alkalmazás azonossága ettől nem sérül, mert a
    manifest kiírja a rögzített `id`-t.
  */
  setLink('link[rel="manifest"]', `/manifest.webmanifest?ikon=${icon}`);

  writeCookie(icon);
  void setDesktopAppIcon(icon);
}

export function useAppIcon() {
  const [icon, setIcon] = useState<AppIcon>(readAppIcon);

  useEffect(() => {
    apply(icon);
    try {
      localStorage.setItem(KEY, icon);
    } catch {
      // A tárolás hiánya nem törheti el a váltást.
    }
  }, [icon]);

  const select = useCallback((next: AppIcon) => setIcon(next), []);

  return { icon, setIcon: select };
}

/** Első festés előtt fut, hogy ne villanjon az alapértelmezett ikon a fülön. */
export function applyStoredAppIconEarly(): void {
  apply(readAppIcon());
}

/**
 * A szerverről érkező beállítás átvezetése.
 *
 * Másik gépen váltott ikont a felhasználó? Bejelentkezés után ez állítja be.
 * Csak akkor ír, ha tényleg más — különben minden session-lekérés újra
 * lecserélné a linkeket és újra elküldené a képet az asztali appnak.
 */
export function syncAppIconFromServer(icon: AppIcon): void {
  if (readAppIcon() === icon) return;

  try {
    localStorage.setItem(KEY, icon);
  } catch {
    // lásd fent
  }
  apply(icon);
}
